/**
 * Step3ServiceAreas
 * Service coverage: province multi-select, city multi-select per province,
 * service radius, remote/virtual toggle, languages offered, map placeholder.
 */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import MultiSelect from '../../components/MultiSelect';

const PROVINCES = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

const CITIES_BY_PROVINCE = {
  AB: ['Calgary', 'Edmonton', 'Red Deer', 'Lethbridge', 'Medicine Hat', 'Fort McMurray', 'Grande Prairie'],
  BC: ['Vancouver', 'Victoria', 'Kelowna', 'Kamloops', 'Nanaimo', 'Prince George', 'Abbotsford', 'Surrey'],
  MB: ['Winnipeg', 'Brandon', 'Steinbach', 'Thompson', 'Portage la Prairie'],
  NB: ['Moncton', 'Saint John', 'Fredericton', 'Bathurst', 'Miramichi'],
  NL: ["St. John's", 'Corner Brook', 'Mount Pearl', 'Conception Bay South', 'Grand Falls-Windsor'],
  NS: ['Halifax', 'Dartmouth', 'Sydney', 'Truro', 'New Glasgow'],
  NT: ['Yellowknife', 'Hay River', 'Inuvik', 'Fort Smith'],
  NU: ['Iqaluit', 'Rankin Inlet', 'Arviat', 'Baker Lake'],
  ON: ['Toronto', 'Ottawa', 'Mississauga', 'Brampton', 'Hamilton', 'London', 'Markham', 'Vaughan', 'Kitchener', 'Windsor', 'Waterloo', 'Barrie', 'Guelph', 'Kingston', 'Thunder Bay'],
  PE: ['Charlottetown', 'Summerside', 'Stratford'],
  QC: ['Montreal', 'Quebec City', 'Laval', 'Gatineau', 'Longueuil', 'Sherbrooke', 'Saguenay', 'Trois-Rivieres'],
  SK: ['Saskatoon', 'Regina', 'Prince Albert', 'Moose Jaw', 'Swift Current'],
  YT: ['Whitehorse', 'Dawson City', 'Watson Lake'],
};

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
  { value: 'en-fr', label: 'English & French (Bilingual)' },
  { value: 'zh', label: 'Mandarin/Cantonese' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'es', label: 'Spanish' },
  { value: 'tl', label: 'Tagalog' },
  { value: 'ar', label: 'Arabic' },
  { value: 'other', label: 'Other' },
];

const RADIUS_OPTIONS = [
  { value: '', label: 'Not applicable' },
  { value: '10', label: '10 km' },
  { value: '25', label: '25 km' },
  { value: '50', label: '50 km' },
  { value: '100', label: '100 km' },
  { value: '250', label: '250 km' },
  { value: '500', label: '500+ km' },
];

export function validateStep3(data) {
  const errors = {};
  if (!data.provinces || data.provinces.length === 0) {
    errors.provinces = 'Please select at least one province or territory';
  }
  if (!data.languages || data.languages.length === 0) {
    errors.languages = 'Please select at least one language';
  }
  return errors;
}

export default function Step3ServiceAreas({ data, onChange, errors }) {
  const allCanada = data.provinces && data.provinces.length === PROVINCES.length;

  const handleProvinceChange = useCallback((provinces) => {
    onChange({ ...data, provinces });
  }, [data, onChange]);

  const handleAllCanadaToggle = useCallback(() => {
    if (allCanada) {
      onChange({ ...data, provinces: [], cities: {} });
    } else {
      const allProvinces = PROVINCES.map((p) => p.value);
      onChange({ ...data, provinces: allProvinces });
    }
  }, [allCanada, data, onChange]);

  const handleCityChange = useCallback((province, cities) => {
    onChange({
      ...data,
      cities: { ...data.cities, [province]: cities },
    });
  }, [data, onChange]);

  const handleAllCitiesToggle = useCallback((province) => {
    const allCities = CITIES_BY_PROVINCE[province] || [];
    const currentCities = data.cities?.[province] || [];
    const isAllSelected = currentCities.length === allCities.length;
    onChange({
      ...data,
      cities: {
        ...data.cities,
        [province]: isAllSelected ? [] : [...allCities],
      },
    });
  }, [data, onChange]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    onChange({ ...data, [name]: type === 'checkbox' ? checked : value });
  }, [data, onChange]);

  const handleLanguageChange = useCallback((languages) => {
    onChange({ ...data, languages });
  }, [data, onChange]);

  const selectedProvinces = data.provinces || [];

  // Build city options for each selected province
  const cityOptionsByProvince = useMemo(() => {
    const result = {};
    selectedProvinces.forEach((prov) => {
      const cities = CITIES_BY_PROVINCE[prov] || [];
      result[prov] = cities.map((c) => ({ value: c, label: c }));
    });
    return result;
  }, [selectedProvinces]);

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={sectionTitleStyle}>Service Coverage Areas</h2>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Define where your services are available across Canada.
        </p>
      </div>

      {/* All Canada toggle */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          padding: '12px 16px',
          borderRadius: '10px',
          border: allCanada ? '2px solid #3b82f6' : '1px solid #d1d5db',
          backgroundColor: allCanada ? '#eff6ff' : '#fff',
        }}>
          <input
            type="checkbox"
            checked={allCanada}
            onChange={handleAllCanadaToggle}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>All Canada</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Service available in all provinces and territories</div>
          </div>
        </label>
      </div>

      {/* Province multi-select */}
      <div style={{ marginBottom: '20px' }}>
        <MultiSelect
          label="Provinces & Territories"
          options={PROVINCES}
          selected={selectedProvinces}
          onChange={handleProvinceChange}
          placeholder="Select provinces..."
          required
          error={errors.provinces}
        />
      </div>

      {/* City selection per selected province */}
      {selectedProvinces.length > 0 && (
        <section style={{
          backgroundColor: '#f8fafc',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '20px',
        }}>
          <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
            Cities per Province
          </h3>
          <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
            Optionally select specific cities. Leave empty to cover all cities in the province.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {selectedProvinces.map((prov) => {
              const provinceName = PROVINCES.find((p) => p.value === prov)?.label || prov;
              const allCities = CITIES_BY_PROVINCE[prov] || [];
              const selectedCities = data.cities?.[prov] || [];
              const isAll = selectedCities.length === allCities.length && allCities.length > 0;

              return (
                <div key={prov} style={{
                  padding: '12px',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: allCities.length > 0 ? '8px' : '0',
                  }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
                      {provinceName}
                    </span>
                    {allCities.length > 0 && (
                      <button
                        type="button"
                        onClick={() => handleAllCitiesToggle(prov)}
                        style={{
                          padding: '2px 8px',
                          fontSize: '11px',
                          fontWeight: '600',
                          color: isAll ? '#ef4444' : '#3b82f6',
                          backgroundColor: isAll ? '#fef2f2' : '#f0f9ff',
                          border: `1px solid ${isAll ? '#fecaca' : '#bfdbfe'}`,
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        {isAll ? 'Clear' : 'All Cities'}
                      </button>
                    )}
                  </div>
                  {allCities.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {allCities.map((city) => {
                        const isSelected = selectedCities.includes(city);
                        return (
                          <button
                            key={city}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                handleCityChange(prov, selectedCities.filter((c) => c !== city));
                              } else {
                                handleCityChange(prov, [...selectedCities, city]);
                              }
                            }}
                            style={{
                              padding: '4px 10px',
                              fontSize: '12px',
                              fontWeight: '500',
                              borderRadius: '6px',
                              border: isSelected ? '1px solid #3b82f6' : '1px solid #d1d5db',
                              backgroundColor: isSelected ? '#eff6ff' : '#fff',
                              color: isSelected ? '#1e40af' : '#475569',
                              cursor: 'pointer',
                            }}
                          >
                            {city}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Service radius */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Service Radius</label>
          <select
            name="serviceRadius"
            value={data.serviceRadius || ''}
            onChange={handleChange}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {RADIUS_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>
            Applicable if services are location-dependent
          </div>
        </div>

        {/* Remote/virtual toggle */}
        <div style={{ flex: '1 1 200px', display: 'flex', alignItems: 'flex-end' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '10px 16px',
            borderRadius: '8px',
            border: data.remoteServices ? '2px solid #3b82f6' : '1px solid #d1d5db',
            backgroundColor: data.remoteServices ? '#eff6ff' : '#fff',
            width: '100%',
          }}>
            <input
              type="checkbox"
              name="remoteServices"
              checked={data.remoteServices || false}
              onChange={handleChange}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Remote / Virtual Services</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>We offer services online or remotely</div>
            </div>
          </label>
        </div>
      </div>

      {/* Languages */}
      <div style={{ marginBottom: '20px' }}>
        <MultiSelect
          label="Languages Offered"
          options={LANGUAGES}
          selected={data.languages || []}
          onChange={handleLanguageChange}
          placeholder="Select languages..."
          required
          error={errors.languages}
        />
      </div>

      {/* Map visualization placeholder */}
      <div style={{
        padding: '40px',
        borderRadius: '10px',
        border: '2px dashed #d1d5db',
        backgroundColor: '#f8fafc',
        textAlign: 'center',
      }}>
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ margin: '0 auto 12px' }}>
          <path d="M20 4C13.373 4 8 9.373 8 16C8 25 20 36 20 36C20 36 32 25 32 16C32 9.373 26.627 4 20 4Z" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="20" cy="16" r="4" stroke="#94a3b8" strokeWidth="2" />
        </svg>
        <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 4px', fontWeight: '500' }}>
          Map Visualization
        </p>
        <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>
          An interactive map showing your service coverage areas will be displayed here.
        </p>
      </div>
    </div>
  );
}

Step3ServiceAreas.propTypes = {
  data: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
};

const sectionTitleStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#1e293b',
  marginBottom: '6px',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  color: '#1e293b',
  outline: 'none',
  boxSizing: 'border-box',
};
